import { INV_CHART } from '../../theme';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, FormControlLabel, Switch, Typography } from '@mui/material';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import FilterToolbar from '../../components/FilterToolbar';
import SectionCard from '../../components/SectionCard';
import StatCard from '../../components/StatCard';
import BatchChip from '../../components/BatchChip';
import InvButton from '../../components/InvButton';
import InvDataGrid from '../../components/InvDataGrid';
import { INV_ICONS } from '../../constants/icons';
import { getStores, getStockBalances, getLedger } from '../../api/stockLedger';
import useAssignedStoreOptions from '../../hooks/useAssignedStoreOptions';
import useItemNames from '../../hooks/useItemNames';
import { formatDateTime } from '../../utils';
import { IPD_COLORS, switchSx, INV_SPACING } from '../../theme';
import dayjs from 'dayjs';

const StoreQueuesIcon = INV_ICONS.storeQueues;

const STORE_STORAGE_KEY = 'inv_selected_store';

// Module scope: it never changes, and inside the component it was a fresh object each
// render that the ledgerColumns useMemo silently closed over.
const MOVEMENT_COLORS = {
  RECEIPT: IPD_COLORS.success.text, ISSUE: IPD_COLORS.warning.text,
  TRANSFER_IN: IPD_COLORS.primary, TRANSFER_OUT: IPD_COLORS.textSecondary,
  RETURN: INV_CHART.purple, CONSUMPTION: IPD_COLORS.error.text,
  ADJUSTMENT: IPD_COLORS.info.text, RESERVATION: IPD_COLORS.warning.text,
};

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

function readStoredStore() {
  try {
    return sessionStorage.getItem(STORE_STORAGE_KEY) || '';
  } catch { return ''; }
}

export default function StockBalances() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const { resolveItem } = useItemNames();
  const params = new URLSearchParams(location.search);
  // This page sits one level below the module base; every link below is relative to
  // the module, not to this route.
  const stockBase = url.replace(/\/balances\/?$/, '');

  const [stores, setStores] = useState([]);
  const {
    storeLocked, activeStoreId, setActiveStoreId, isStoreScoped, storeOptions,
  } = useAssignedStoreOptions(stores);
  // Scoped users start on their active assigned store. Unscoped keep URL / last pick.
  const [selectedStore, setSelectedStore] = useState(
    activeStoreId || params.get('store_id') || readStoredStore(),
  );
  const [stockBalances, setStockBalances] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(false);

  useEffect(() => {
    getStores().then(d => {
      const arr = normalizeList(d);
      setStores(arr);
      if (storeLocked) return;
      if (isStoreScoped && activeStoreId) {
        setSelectedStore(activeStoreId);
        return;
      }
      // Only fall back to the first store when nothing was picked before —
      // getStores() has no documented ordering, so "first" can change per session.
      const known = arr.some(s => (s.store_id || s.id) === selectedStore);
      if (!known && arr.length > 0) {
        setSelectedStore(arr[0].store_id || arr[0].id);
      }
    }).catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Nothing to remember for a scoped user, and writing it would leak their store
    // into the selector of any other account sharing the tab.
    if (!selectedStore || isStoreScoped) return;
    try { sessionStorage.setItem(STORE_STORAGE_KEY, selectedStore); } catch { /* ignore */ }
  }, [selectedStore, isStoreScoped]);

  useEffect(() => {
    if (isStoreScoped && activeStoreId) setSelectedStore(activeStoreId);
  }, [isStoreScoped, activeStoreId]);

  const loadStock = useCallback(async () => {
    if (!selectedStore) return;
    setStockLoading(true);
    try {
      const p = { store_id: selectedStore };
      if (onlyAvailable) p.only_available = true;
      const data = await getStockBalances(p);
      setStockBalances(Array.isArray(data) ? data : (data?.items || []));
    } catch { /* keep prior */ } finally {
      setStockLoading(false);
    }
  }, [selectedStore, onlyAvailable]);

  const loadLedger = useCallback(async () => {
    if (!selectedStore) return;
    setLedgerLoading(true);
    try {
      const data = await getLedger({ store_id: selectedStore, limit: 10 });
      setLedger(Array.isArray(data) ? data : (data?.items || []));
    } catch { /* keep prior */ } finally {
      setLedgerLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    loadStock();
    loadLedger();
  }, [loadStock, loadLedger]);

  const filtered = stockBalances.filter(row =>
    !search || resolveItem(row).toLowerCase().includes(search.toLowerCase()),
  );

  const lowStock = filtered.filter(r => (r.available_quantity || 0) < 10).length;
  const expiringSoon = filtered.filter(r => {
    if (!r.expiry_date) return false;
    return dayjs(r.expiry_date).diff(dayjs(), 'day') <= 30;
  }).length;

  const stockColumns = useMemo(() => [
    {
      field: 'item_name', headerName: 'Item', flex: 1, minWidth: 160,
      valueGetter: p => resolveItem(p.row),
    },
    {
      field: 'batch', headerName: 'Batch', width: 200,
      renderCell: p => <BatchChip batchNumber={p.row.batch_number} expiryDate={p.row.expiry_date} />,
    },
    { field: 'on_hand_quantity', headerName: 'On Hand', width: 100, align: 'right', headerAlign: 'right' },
    {
      field: 'available_quantity', headerName: 'Available', width: 100, align: 'right', headerAlign: 'right',
      renderCell: p => (
        <Typography sx={{
          color: (p.value || 0) < 10 ? IPD_COLORS.warning.text : IPD_COLORS.textPrimary,
          fontWeight: (p.value || 0) < 10 ? 700 : 400,
          fontSize: 13,
        }}>
          {p.value}
        </Typography>
      ),
    },
    { field: 'reserved_quantity', headerName: 'Reserved', width: 100, align: 'right', headerAlign: 'right' },
  ], [resolveItem]);

  const ledgerColumns = useMemo(() => [
    { field: 'created_at', headerName: 'Date', width: 140, valueFormatter: p => formatDateTime(p.value) },
    {
      field: 'movement_type', headerName: 'Type', width: 140,
      renderCell: p => (
        <Typography sx={{ color: MOVEMENT_COLORS[p.value] || IPD_COLORS.textSecondary, fontWeight: 600, fontSize: 12 }}>
          {p.value?.replace(/_/g, ' ')}
        </Typography>
      ),
    },
    { field: 'item_name', headerName: 'Item', flex: 1, valueGetter: p => resolveItem(p.row) },
    {
      field: 'quantity_in', headerName: 'In', width: 80, align: 'right', headerAlign: 'right',
      renderCell: p => (p.value
        ? <Typography sx={{ color: IPD_COLORS.success.text, fontWeight: 600, fontSize: 13 }}>{p.value}</Typography>
        : '—'),
    },
    {
      field: 'quantity_out', headerName: 'Out', width: 80, align: 'right', headerAlign: 'right',
      renderCell: p => (p.value
        ? <Typography sx={{ color: IPD_COLORS.warning.text, fontWeight: 600, fontSize: 13 }}>{p.value}</Typography>
        : '—'),
    },
  ], [resolveItem]);

  const lockedStoreLabel = storeOptions.find(o => o.value === selectedStore)?.label || '';

  const queuesHref = selectedStore
    ? `${stockBase}/queues?store_id=${encodeURIComponent(selectedStore)}`
    : `${stockBase}/queues`;

  return (
    <Box>
      <PageHeader
        breadcrumb={['Inventory Management', 'Stock Balances']}
        title="Stock Balances"
        description="On-hand, reserved and expiring stock for the selected store"
        actions={
          // Everything else this header used to carry (New Store, Goods Discard,
          // Adjustments, Transfer, Ledger, Opening Stock) is a sidebar item now.
          // Store Queues stays because only this page knows the selected store.
          <InvButton
            startIcon={<StoreQueuesIcon />}
            size="small"
            onClick={() => history.push(queuesHref)}
          >
            Store Queues
          </InvButton>
        }
      />

      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="Search items..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {storeLocked ? (
          <FilterToolbar.StaticValue label="Store" width={280} value={lockedStoreLabel} />
        ) : (
          <FilterToolbar.Filter
            label="Store"
            width={280}
            value={selectedStore}
            onChange={e => {
              setSelectedStore(e.target.value);
              setActiveStoreId(e.target.value);
            }}
            options={storeOptions}
          />
        )}
        <FilterToolbar.Slot>
          <FormControlLabel
            control={(
              <Switch
                checked={onlyAvailable}
                onChange={e => setOnlyAvailable(e.target.checked)}
                sx={switchSx}
              />
            )}
            label="Only Available"
            sx={{
              color: IPD_COLORS.textSecondary,
              mb: 0,
              '& .MuiFormControlLabel-label': { fontSize: 13 },
            }}
          />
        </FilterToolbar.Slot>
      </FilterToolbar>

      <Grid container spacing={INV_SPACING.grid} sx={{ mb: INV_SPACING.section }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total SKUs" value={filtered.length} icon={INV_ICONS.balances} tone="teal" loading={stockLoading} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Low Stock (< 10)" value={lowStock} icon={INV_ICONS.lowStock} tone={lowStock > 0 ? 'amber' : 'teal'} loading={stockLoading} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Expiring ≤ 30 days" value={expiringSoon} icon={INV_ICONS.expiry} tone={expiringSoon > 0 ? 'red' : 'teal'} loading={stockLoading} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Stores" value={stores.length} icon={INV_ICONS.stores} tone="mint" />
        </Grid>
      </Grid>

      <SectionCard title="Stock Balances">
        <InvDataGrid
          rows={filtered}
          columns={stockColumns}
          loading={stockLoading}
          getRowId={r => `${r.item_id || r.id}-${r.batch_number || 'no-batch'}`}
          emptyMessage="No stock found for this store."
          getRowClassName={p => {
            const days = p.row.expiry_date ? dayjs(p.row.expiry_date).diff(dayjs(), 'day') : null;
            if (days !== null && days <= 30) return 'expiry-critical';
            if (days !== null && days <= 90) return 'expiry-warning';
            return '';
          }}
          sx={{
            '& .expiry-critical': { backgroundColor: `${IPD_COLORS.error.text}12` },
            '& .expiry-warning': { backgroundColor: `${IPD_COLORS.warning.text}0a` },
          }}
        />
      </SectionCard>

      <SectionCard title="Recent Movements (Last 10)" sx={{ mt: 2.5 }}>
        <InvDataGrid
          rows={ledger}
          columns={ledgerColumns}
          loading={ledgerLoading}
          getRowId={r => r.ledger_id || r.id || Math.random()}
          emptyMessage="No movements yet."
        />
      </SectionCard>
    </Box>
  );
}
